import { Loader } from '@/components'
import { useSegment } from '@/modules'
import {
  CheckoutBase,
  CheckoutStep,
} from '@/modules/checkout/components/CheckoutBase'
import { FormProvider, useSnackbar } from '@/providers'
import { Redirect } from '@/routes/Redirect'
import { paths } from '@/routes/paths'
import { useCompany, useSubmitCheckout } from '@/services'
import { CheckoutResponse } from '@/types/cart'
import { CartRecommendationProduct } from '@/types/recommendation'
import {
  checkIsInPartnerUrl,
  noop,
  useModal,
  useStateObject,
  useSteps,
} from '@/utils'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOutletContext, useParams } from 'react-router-dom'
import { SuccessPage } from '../SelfAssessmentPage/public/SuccessPage'
import { ThankYouPage } from '../SelfAssessmentPage/public/ThankYouPage'
import { useCheckoutSteps } from './helpers'
import { CheckoutStepKeys } from './steps'
import { makeValidationSchema } from './validationSchema'

interface ButtonState {
  text: string
  onClick: () => void
  disabled?: boolean
  loading?: boolean
}

interface OutletContext {
  cart: Record<string, CartRecommendationProduct[]>
  user: {
    fullname: string
    birthdate?: string
  }
}

interface CheckoutPageContentProps {
  steps: CheckoutStep[]
  killerQuestions: string[]
  companyId: string
}

interface SubmitCheckoutParams {
  paymentId: string
  items: any[]
}

interface StepState {
  index: number
  step: string
  data: {
    title?: string
    subtitle?: string
    tooltip?: React.ReactNode
    Component?: React.ComponentType<any>
    key?: string
  }
  isFirst: boolean
  isLast: boolean
  back: () => void
  next: () => void
  goTo: (step: string) => void
}

interface ValidationMessages {
  general: string
  invalidIban: string
  noneGermanIban: string
  over18: string
  postalCodeTypeError: string
  payment: string
  [key: string]: string
}

export const CheckoutPageContent = ({
  steps,
  killerQuestions,
  companyId,
}: CheckoutPageContentProps) => {
  const { locationId } = useParams()
  const { cart, user } = useOutletContext<OutletContext>()
  const { data: company, isLoading: isCompanyLoading } = useCompany(companyId)
  const analytics = useSegment()
  const data = useMemo(() => cart[locationId || ''] || [], [cart])
  const isInPartnerUrl = checkIsInPartnerUrl()
  const checkoutMutation = useSubmitCheckout()
  const snackbar = useSnackbar()

  const { t } = useTranslation()
  const validations = t('validations', {
    returnObjects: true,
  }) as ValidationMessages

  const stepState = useSteps({
    initialStep: CheckoutStepKeys.overview,
    steps,
    keyPropName: 'key',
  }) as StepState

  const contactState = useModal()

  const [buttonState, setButtonState] = useStateObject<ButtonState>({
    text: t('next'),
    onClick: stepState.isLast ? noop : stepState?.next,
  })
  const [
    secondaryButtonState,
    setSecondaryButtonState,
  ] = useStateObject<ButtonState>({
    text: t('iDoNotConfirm'),
    onClick: noop,
  })

  const [protocolDownloadUrl, setProtocolDownloadUrl] = useState<string>()

  const handleCheckoutSuccess = ({
    consultationProtocolUrl,
  }: CheckoutResponse) => {
    if (consultationProtocolUrl) {
      setProtocolDownloadUrl(consultationProtocolUrl)
    }
    analytics.track('app_checkout_completed')
    successState.open()
  }

  const handleSubmitCheckout = ({ paymentId, items }: SubmitCheckoutParams) => {
    checkoutMutation.mutate(
      {
        paymentId,
        items,
      },
      {
        onSuccess: handleCheckoutSuccess,
        onError: () => {
          contactState.open()
          snackbar.error(t('snackbars.error.generalMsg'))
        },
      },
    )
  }

  const successState = useModal()

  const { Component } = useMemo(() => stepState.data, [stepState.data?.key])

  if (contactState.isOpen) return <SuccessPage />

  if (successState.isOpen)
    return (
      <ThankYouPage
        isInPartnerUrl={isInPartnerUrl}
        protocolDownloadUrl={protocolDownloadUrl}
      />
    )

  if (!Component || isCompanyLoading) {
    return <Loader />
  }

  return (
    <CheckoutBase
      buttonState={buttonState}
      secondaryButtonState={secondaryButtonState}
      data={data}
      isPublic={false}
      steps={steps}
      stepState={stepState}
    >
      <FormProvider
        initialValues={{
          fullname: user.fullname,
          birthdate: user.birthdate ? Date.parse(user.birthdate) : null,
          companyDetails: {
            name: company?.name,
            address: {
              street: company?.address?.street,
              streetNr: company?.address?.streetNr,
              postalCode: company?.address?.postalCode,
              city: company?.address?.city,
              country: company?.address?.country,
            },
          },
        }}
        validationSchema={makeValidationSchema(validations)[stepState.step]}
      >
        <Component
          companyId={companyId}
          goTo={stepState.goTo}
          killerQuestions={killerQuestions}
          onNext={stepState.next}
          isLast={stepState.isLast}
          openContact={contactState.open}
          submitCheckout={handleSubmitCheckout}
          submitCheckoutLoading={checkoutMutation.isLoading}
          openSuccess={successState.open}
          setButtonState={setButtonState}
          poaSigned={company?.poaSigned}
          setSecondaryButtonState={setSecondaryButtonState}
        />
      </FormProvider>
    </CheckoutBase>
  )
}

export const CheckoutPage = () => {
  const { locationId } = useParams()
  const { cart } = useOutletContext<OutletContext>()
  const data = cart[locationId || '']

  const initialCount = data?.length

  const { isLoading, steps, killerQuestions, companyId } = useCheckoutSteps(
    data,
  )

  if (isLoading) return <Loader />

  if (data && !initialCount)
    return <Redirect to={paths.viewCartWithKey(locationId || '')} />

  return (
    <CheckoutPageContent
      companyId={companyId || ''}
      killerQuestions={killerQuestions}
      steps={steps}
    />
  )
}
